import React, { FunctionComponent } from "react";
import { Formik, Form } from "formik";
import { styled } from "@iventis/styles";
import * as Yup from "yup";
import { Content } from "@iventis/translations/content/typed-content";
import { TextField } from "@mui/material";
import { FormLabel } from "@iventis/styles/src/components/texts";
import { formFieldVerticalGap } from "@iventis/styles/src/atomic-rules";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { PasswordValidator, TermsAndConditionsCheckbox } from "@iventis/components";
import { regexDigit, regexLowercase, regexUppercase } from "@iventis/utilities";
import { useHistory } from "@iventis/subscriptions";
import { StyledResetButton } from "./forgotten-password-form";

export const SetPasswordForm: FunctionComponent<{ handlePasswordSubmit: (newPassword: string) => Promise<void> }> = ({ handlePasswordSubmit }) => {
    const translate = useIventisTranslate();
    const history = useHistory();
    return (
        <Formik
            initialValues={{
                password: "",
                passwordConfirm: "",
                termsAndConditions: false,
            }}
            validationSchema={Yup.object().shape({
                password: Yup.string()
                    .required(translate(Content.login.error.required))
                    .min(8, " ")
                    .max(64, translate(Content.login.error.passwordTooLong))
                    .matches(regexLowercase, " ")
                    .matches(regexUppercase, " ")
                    .matches(regexDigit, " "),
                passwordConfirm: Yup.string()
                    .oneOf([Yup.ref("password")], translate(Content.login.error.passwordsMustMatch))
                    .required(translate(Content.login.error.required)),
                termsAndConditions: Yup.bool().required(translate(Content.login.error.mustAgreeToTC)).oneOf([true], translate(Content.login.error.mustAgreeToTC)),
            })}
            // this is necessary for keeping the SUBMIT button disabled until Form is valid
            validateOnMount
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                try {
                    await handlePasswordSubmit(values.password);
                    history.push("/login");
                } catch (e) {
                    if (e?.response?.data === "Password Not Valid") {
                        toast.error({
                            title: translate(Content.errors.error),
                            message: translate(Content.login2.passwordConditions.commonPassword),
                        });
                    } else {
                        toast.error({
                            title: translate(Content.errors.error),
                            message: translate(Content.login.error.failed),
                        });
                    }
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(props) => {
                const { errors, values, touched, handleChange, handleSubmit, handleBlur, isSubmitting } = props;
                return (
                    <StyledSetPasswordForm
                        onSubmit={handleSubmit}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                        }}
                        placeholder=""
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="text-entry">
                            <StyledFieldLabel>
                                <FormLabel>{translate(Content.login.labels.password)}</FormLabel>
                            </StyledFieldLabel>
                            <TextField
                                className="form-text-field"
                                id="password-input"
                                variant="outlined"
                                aria-labelledby="password-label"
                                type="password"
                                name="password"
                                data-cy="password"
                                placeholder={translate(Content.login.placeholders.passwordCreate)}
                                size="small"
                                value={values.password}
                                error={errors.password && touched.password}
                                inputProps={{
                                    "data-testid": "password",
                                }}
                                aria-label="password"
                                hiddenLabel
                                onChange={handleChange}
                                onBlur={handleBlur}
                                autoComplete="new-password"
                            />
                        </div>

                        <PasswordValidator password={values.password} stacked />
                        <div className="text-entry">
                            <StyledFieldLabel htmlFor="password-confirmation">
                                <FormLabel>{translate(Content.login.labels.confirmPass)}</FormLabel>
                            </StyledFieldLabel>
                            <TextField
                                className="form-text-field"
                                id="password-confirmation"
                                variant="outlined"
                                aria-labelledby="passwordConfirm-label"
                                type="password"
                                name="passwordConfirm"
                                placeholder={translate(Content.login.placeholders.passwordConfirm)}
                                size="small"
                                inputProps={{
                                    "data-testid": "password-confirm",
                                }}
                                aria-label="password-confirm"
                                hiddenLabel
                                max-length={64}
                                value={values.passwordConfirm}
                                error={errors.passwordConfirm && touched.passwordConfirm}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                helperText={errors.passwordConfirm && touched.passwordConfirm && errors.passwordConfirm}
                            />
                        </div>
                        <TermsAndConditionsCheckbox onBlur={handleBlur} onChange={handleChange} errors={errors} touched={touched} forgottenPasswordCheckbox />
                        <StyledResetButton disabled={isSubmitting} type="submit">
                            {translate(Content.login.buttons.continue)}
                        </StyledResetButton>
                    </StyledSetPasswordForm>
                );
            }}
        </Formik>
    );
};

const StyledSetPasswordForm = styled(Form)`
    .text-entry {
        margin-top: ${formFieldVerticalGap};
        .form-text-field {
            min-width: 100%;
        }
    }
`;
